import { onBeforeUnmount, onMounted, ref, watch } from "vue";

/**
 * Auto-pagination: put the returned `sentinel` ref on an element at the list
 * bottom; `onReach` fires whenever it scrolls into view (with a margin so the
 * next page starts loading before the user actually hits the end). Replaces
 * the manual "โหลดเพิ่ม" button — Drive-style lists just keep going.
 */
export function useInfiniteScroll(onReach: () => void) {
  const sentinel = ref<HTMLElement | null>(null);
  let observer: IntersectionObserver | null = null;

  onMounted(() => {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onReach();
      },
      { rootMargin: "400px 0px" } // prefetch one screen ahead of the scroll edge
    );
    if (sentinel.value) observer.observe(sentinel.value);
  });

  // The sentinel sits inside v-if/v-else branches (loading/empty/list), so the
  // element comes and goes — re-observe whenever it changes rather than only at mount.
  watch(sentinel, (el) => {
    if (!observer) return;
    observer.disconnect();
    if (el) observer.observe(el);
  });

  onBeforeUnmount(() => observer?.disconnect());

  return { sentinel };
}
